<template>
  <div>
    <div v-if="!submitted" class="columns">
      <b-field label="Course name" class="column is-half">
        <b-input
          v-model="course.title"
          required
          type="text"
          name="title"
        ></b-input>
      </b-field>

      <b-field label="Find a university" class="column is-half">
        <b-autocomplete
          v-model="course.university.title"
          field="title"
          open-on-focus
          :data="universities"
          :clearable="true"
          @select="(option) => changedUniversity(option)"
          @typing="queryUniversities"
        >
          <template slot-scope="props">
            <div class="media">
              <div class="media-left">
                <img width="32" :src="props.option.img_url" />
              </div>
              <div class="media-content">
                {{ props.option.title }}
                <br />
                <small> Address: {{ props.option.address }} </small>
                <br />
              </div>
            </div>
          </template>
        </b-autocomplete>
      </b-field>

      <b-field
        label="Course deadline"
        class="column is-one-third deadline-column"
      >
        <b-datepicker
          v-if="!deadlineTextMode"
          v-model="course.templateOptions.deadline"
          locale="en-GB"
          :show-week-number="true"
          placeholder="Click to select..."
          icon="calendar-today"
          :icon-right="course.templateOptions.deadline ? 'close-circle' : ''"
          icon-right-clickable
          @icon-right-click="clearDate"
          trap-focus
        >
        </b-datepicker>
        <b-input
          v-else
          v-model="course.templateOptions.deadline"
          type="text"
          name="deadline_text"
        ></b-input>

        <b-field>
          <b-radio-button
            v-model="deadlineType"
            native-value="date"
            type="is-primary is-light is-outlined"
            @input="changeDeadlineType('date')"
          >
            <b-icon icon="calendar"></b-icon>
            <span>Date</span>
          </b-radio-button>

          <b-radio-button
            v-model="deadlineType"
            native-value="open"
            type="is-success is-light is-outlined"
            @input="changeDeadlineType('open')"
          >
            <b-icon icon="check"></b-icon>
            <span>Open</span>
          </b-radio-button>

          <b-radio-button
            v-model="deadlineType"
            native-value="closed"
            type="is-danger is-light is-outlined"
            @input="changeDeadlineType('closed')"
          >
            <b-icon icon="close"></b-icon>
            Closed
          </b-radio-button>
        </b-field>
      </b-field>

      <b-field label="Student Finance" class="column is-one-third">
        <b-input
          v-model="course.templateOptions.student_finance"
          type="text"
          name="student_finance"
        ></b-input>
      </b-field>

      <b-field
        label="Standard or Non-standard route"
        class="column is-one-third"
      >
        <b-switch
          v-model="course.templateOptions.standard_route"
          false-value="Standard route"
          true-value="Non-standard route"
          class="column is-full"
        >
          {{ course.templateOptions.standard_route }}
        </b-switch>
      </b-field>

      <b-field label="Timetable" class="column is-half">
        <b-input
          placeholder="Use shift-enter for new line"
          type="textarea"
          v-model="course.templateOptions.timetable"
          name="timetable"
        ></b-input>
      </b-field>

      <b-field label="Admission Proccess" class="column is-half">
        <b-input
          placeholder="Use shift-enter for new line"
          type="textarea"
          v-model="course.templateOptions.admission_process"
          name="timetable"
        ></b-input>
      </b-field>

      <div class="block column is-full">
        <label class="label">
          Timetable Options:
          <b-icon
            @click.native.prevent="
              course.templateOptions.timetable_options = []
            "
            icon="lock-reset"
            size="is-small"
            type="is-danger"
            title="Reset timetable"
            class="cursor-pointer"
          ></b-icon>
        </label>
        <b-checkbox
          v-for="(timetable, index) in timetableOptions"
          v-bind:key="index"
          v-model="course.templateOptions.timetable_options"
          :native-value="timetable"
        >
          {{ timetable }}
        </b-checkbox>
      </div>

      <div class="block column is-full">
        <label class="label"> Documents: </label>
        <b-checkbox
          v-for="(document, index) in customDocuments"
          v-bind:key="index"
          v-model="course.templateOptions.documents"
          :native-value="document.text"
        >
          {{ document.text }}
          <b-icon
            v-if="document.isRemovable"
            @click.native.prevent="removeCustomDocuments(index, document)"
            icon="close"
            size="is-small"
            type="is-danger"
          >
          </b-icon>
        </b-checkbox>
        <b-field
          label="Add custom document"
          class="column is-half no-padding-left"
        >
          <b-input
            v-model="customDocument"
            @keyup.native.enter="addCustomDocument()"
            type="text"
            name="custom_doc"
          ></b-input>
        </b-field>
      </div>

      <b-button class="btn" @click="$router.go(-1)" style="margin: 0 15px"
        >Go back</b-button
      >
      <b-button
        v-if="isAdmin"
        :disabled="isSubmitDisabled"
        @click="saveCourse"
        type="is-primary"
        >Submit</b-button
      >
      <p v-if="mappedIds.length" class="block column is-full number-id">
        These changes will apply to {{ mappedIds.length }} courses.
      </p>
    </div>

    <section v-else class="hero is-success">
      <div class="hero-body">
        <p class="title">You submitted successfully!</p>
        <p class="subtitle">
          <b-button @click="newCourse" type="is-primary"
            >Add new course</b-button
          >
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { deadlineInvalidDates } from '../../models/static';

import CourseService from '../../services/course.service';
import UniversityService from '../../services/university.service';
import UtilService from '../../services/util';

import moment from 'moment';

export default {
  name: 'add-course',
  props: ['courseId'],
  data() {
    return {
      course: {
        id: null,
        title: '',
        university: {},
        templateOptions: {
          deadline: null,
          documents: [],
          timetable_options: [],
          offer: '',
          timetable: '',
          admission_process: '',
          student_finance: '',
          standard_route: 'Standard route',
        },
      },
      deadlineType: 'date',
      submitted: false,
      universities: [],
      originalUniversities: [],
      customDocuments: [
        {
          text: 'Passport',
          isRemovable: false,
        },
        {
          text: 'Diploma+Transcript',
          isRemovable: false,
        },
        {
          text: 'CV',
          isRemovable: false,
        },
        {
          text: 'Non standard route document',
          isRemovable: false,
        },
        {
          text: 'Personal Statement',
          isRemovable: false,
        },
        {
          text: 'Share Code',
          isRemovable: false,
        },
        {
          text: 'Proof of Address',
          isRemovable: false,
        },
        {
          text: 'Ask33 Consent Form',
          isRemovable: false,
        },
        {
          text: 'Checklist',
          isRemovable: false,
        },
        {
          text: 'SFE Pre-Application Screenshot',
          isRemovable: false,
        },
      ],
      timetableOptions: ['day', 'evening', 'weekend', 'blended'],
      customDocument: '',
      mappedIds: [],
      changedStandardRoute: false,
    };
  },
  mounted() {
    const mappedIds = this.$route.params.mappedIds;
    if (mappedIds && mappedIds !== 'undefined') {
      this.mappedIds = mappedIds;
    }
    this.retrieveUniversities();
  },
  computed: {
    isSubmitDisabled() {
      if (this.mappedIds.length) {
        return false;
      }
      return !this.course.title || !this.course.university.id;
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return (
          this.currentUser.roles.includes('ROLE_ADMIN') ||
          this.currentUser.roles.includes('ROLE_MODERATOR')
        );
      }

      return false;
    },
    deadlineTextMode() {
      return (
        this.deadlineType.toLowerCase() === 'open' ||
        this.deadlineType.toLowerCase() === 'closed'
      );
    },
  },
  watch: {
    '$route.params'() {
      if (this.isAdmin) {
        this.retrieveUniversities();
      }
    },
    'course.templateOptions.standard_route': {
      handler(value, newValue) {
        if (value === newValue) {
          this.changedStandardRoute = false;
        } else {
          this.changedStandardRoute = true;
        }
      },
      deep: true,
    },
  },
  methods: {
    resetCourse() {
      this.course = {
        id: null,
        title: '',
        universityId: null,
        templateOptions: {
          deadline: null,
          documents: [],
          timetable_options: [],
          offer: '',
          timetable: '',
          admission_process: '',
          student_finance: '',
          standard_route: 'Standard route',
        },
        university: {
          title: '',
        },
      };

      this.changedStandardRoute = false;
    },
    checkUrl() {
      let currentUrl = window.location.pathname.split('/')[2];
      if (this.courseId) {
        currentUrl = this.courseId;
      }

      if (this.mappedIds.length) {
        return;
      }

      if (Number.isInteger(+currentUrl)) {
        CourseService.get(+currentUrl).then((response) => {
          this.course.id = response.data.id;
          this.course.title = response.data.title;
          // this.course.universityId = this.getUniversityById(response.data.universityId)
          this.course.university = response.data.university;

          const parsedOptions = JSON.parse(response.data.templateOptions);
          if (parsedOptions) {
            this.course.templateOptions = {
              documents: parsedOptions.documents,
              offer: parsedOptions.offer,
              timetable: parsedOptions.timetable,
              admission_process: parsedOptions.admission_process,
              student_finance: parsedOptions.student_finance,
              timetable_options: parsedOptions.timetable_options,
              standard_route: parsedOptions.standard_route,
              deadline: this.getDeadline(parsedOptions.deadline),
            };
          }

          parsedOptions.documents
            .filter(
              (x) =>
                !this.customDocuments
                  .map((document) => document.text)
                  .includes(x)
            )
            .forEach((element) => {
              this.customDocuments.push({
                text: element,
                isRemovable: true,
              });
            });
        });
      } else {
        this.resetCourse();
      }
    },
    retrieveUniversities() {
      if (!this.isAdmin) {
        return;
      }

      if (!this.universities.length) {
        UniversityService.getAll().then((response) => {
          this.universities = response.data;
          this.originalUniversities = response.data;
          this.checkUrl();
        });
      } else {
        this.checkUrl();
      }
    },
    addCustomDocument() {
      this.customDocuments.push({
        text: this.customDocument,
        isRemovable: true,
      });
      this.customDocument = '';
    },
    removeCustomDocuments(index, document) {
      const indexToRemoveFromDocs =
        this.course.templateOptions.documents.indexOf(document);

      if (indexToRemoveFromDocs > -1) {
        this.course.templateOptions.documents.splice(indexToRemoveFromDocs, 1);
      }

      this.customDocuments.splice(index, 1);
    },
    queryUniversities(queryString) {
      this.universities = queryString.length
        ? [...this.originalUniversities].filter(
            (university) =>
              university.title
                .toLowerCase()
                .includes(queryString.toLowerCase()) ||
              university.address
                .toLowerCase()
                .includes(queryString.toLowerCase())
          )
        : this.originalUniversities;
    },
    getUniversityById(universityId) {
      const foundUni = this.originalUniversities.filter(
        (uni) => uni.id === +universityId
      );
      return foundUni.length ? foundUni[0].title : null;
    },
    saveCourse() {
      let copyTemplate = UtilService.deepCopy(this.course.templateOptions);
      copyTemplate.deadline = !this.deadlineTextMode
        ? moment(this.course.templateOptions.deadline).format('DD-MM-YYYY')
        : this.course.templateOptions.deadline;
      if (this.mappedIds.length) {
        let formData = new FormData();
        let mappedEmpty = Object.fromEntries(
          Object.entries(this.course).filter(
            ([_, v]) => _ && v != null && v != ''
          )
        );
        let mappedEmpty2 = Object.fromEntries(
          Object.entries(copyTemplate).filter(
            ([_, v]) => _ && v != null && v !== '' && v.length !== 0
          )
        );
        delete mappedEmpty.templateOptions;

        if (!this.changedStandardRoute) {
          delete mappedEmpty2.standard_route;
        }

        if (mappedEmpty.universityId) {
          formData.append('universityId', this.course.university.id);
        }

        if (Object.keys(mappedEmpty2).length) {
          formData.append('templateOptions', JSON.stringify(mappedEmpty2));
        }

        formData.append('mappedIds', this.mappedIds);

        CourseService.bulkUpdate(formData).then(() => {
          this.submitted = true;
        });
      } else {
        let formData = new FormData();
        formData.append('title', this.course.title);
        formData.append('universityId', this.course.university.id);
        formData.append('templateOptions', JSON.stringify(copyTemplate));

        if (this.course.id) {
          formData.append('id', this.course.id);
        }

        CourseService.create(formData).then((response) => {
          this.course.id = response.data.id;
          this.submitted = true;
        });
      }
    },

    changedUniversity(option) {
      if (option && option.id) {
        this.course.university = option;
      }
    },

    newCourse() {
      this.submitted = false;
      this.resetCourse();
    },
    clearDate() {
      this.course.templateOptions.deadline = null;
    },

    changeDeadlineType(type) {
      this.deadlineType = type;
      if (type === 'open' || type === 'closed') {
        this.course.templateOptions.deadline = type;
      } else {
        this.course.templateOptions.deadline = new Date();
      }
    },

    getDeadline(deadline) {
      if (
        deadlineInvalidDates.includes(
          deadline.toLowerCase().replace(/["']/g, '')
        )
      ) {
        this.deadlineType = deadline.toLowerCase().replace(/["']/g, '');
        return deadline;
      }

      if (
        new Date(deadline).toString() !== 'Invalid Date' ||
        typeof deadline === 'string' ||
        deadline instanceof String
      ) {
        return moment(deadline.replaceAll('-', '/'), 'DD/MM/YYYY').toDate();
      }

      return null;
    },
  },
};
</script>

<style scoped>
.custom-checkbox span.icon {
  position: absolute;
  right: -20px;
  top: 3px;
  z-index: 10;
}

.columns {
  flex-wrap: wrap;
}

.attachments {
  text-align: center;
}

.tags {
  justify-content: center;
}

.no-padding-left {
  padding-left: 0;
}

.number-id {
  text-align: center;
}
</style>
